import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@crave/ui';

const CSS = `
.crave-scan { border: 0; padding: 0; margin: auto; width: min(520px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px); border-radius: var(--radius); background: var(--paper); color: var(--ink);
  box-shadow: 0 24px 60px rgba(20, 20, 20, 0.28); overflow: auto; overscroll-behavior: contain; }
.crave-scan::backdrop { background: rgba(20, 20, 20, 0.7); }
.crave-scan-body { display: grid; gap: 12px; padding: 20px; }
.crave-scan-title { margin: 0; font-size: 22px; line-height: 1.25; }
.crave-scan-text { margin: 0; color: var(--ink-soft); font-size: 15px; line-height: 1.4; }
.crave-scan-view { position: relative; width: 100%; aspect-ratio: 4 / 3; max-height: 60dvh; border-radius: var(--radius-sm);
  overflow: hidden; background: #141414; }
.crave-scan-view video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.crave-scan-guide { position: absolute; left: 8%; right: 8%; top: 32%; bottom: 32%; border: 3px solid #fff;
  border-radius: 10px; box-shadow: 0 0 0 999px rgba(0, 0, 0, 0.35); pointer-events: none; }
.crave-scan-guide::after { content: ''; position: absolute; left: 6px; right: 6px; top: 50%; height: 2px; background: var(--crave-red); }
.crave-scan-status { position: absolute; left: 0; right: 0; bottom: 10px; margin: 0; text-align: center; color: #fff;
  font-size: 15px; font-weight: 600; text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8); }
.crave-scan-alert { border: 2px solid var(--crave-red); border-radius: var(--radius-sm); padding: 10px 12px; font-size: 15px; }
.crave-scan-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 10px; }
@media (max-width: 480px) {
  .crave-scan-body { padding: 16px; }
  .crave-scan-actions { flex-direction: column-reverse; }
  .crave-scan-actions > button { width: 100%; }
}
`;

/** A zero-suppressed UPC-E (8 digits) as its full 12-digit UPC-A. */
export function expandUpcE(upce: string): string | null {
  if (!/^[01]\d{7}$/.test(upce)) return null;
  const d = upce.slice(1, 7);
  const last = d.charAt(5);
  let body: string;
  if (last === '0' || last === '1' || last === '2')
    body = `${d.slice(0, 2)}${last}0000${d.slice(2, 5)}`;
  else if (last === '3') body = `${d.slice(0, 3)}00000${d.slice(3, 5)}`;
  else if (last === '4') body = `${d.slice(0, 4)}00000${d.charAt(4)}`;
  else body = `${d.slice(0, 5)}0000${last}`;
  return `${upce.charAt(0)}${body}${upce.charAt(7)}`;
}

/** What went wrong with the camera, in a sentence Frank can act on. */
function cameraProblem(err: unknown): string {
  const name = err instanceof Error || err instanceof DOMException ? err.name : '';
  const typeInstead = 'Or tap Type the number instead.';
  switch (name) {
    case 'InsecureContext':
      return `The camera only works on the secure admin site (https). ${typeInstead}`;
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return `Crave is not allowed to use the camera. On iPhone, tap aA in the address bar, then Website Settings, and set Camera to Allow. ${typeInstead}`;
    case 'NotFoundError':
    case 'OverconstrainedError':
      return `No camera was found on this device. ${typeInstead}`;
    case 'NotReadableError':
    case 'AbortError':
      return `The camera is busy in another app. Close that app and try again. ${typeInstead}`;
    case 'ChunkLoadError':
    case 'TypeError':
      return `The scanner did not load. Check the connection and try again. ${typeInstead}`;
    default:
      return `The camera did not start. Close this and try again. ${typeInstead}`;
  }
}

/** Stop every camera track still attached to the video and detach it. */
function releaseVideo(video: HTMLVideoElement | null): void {
  if (!video) return;
  const stream = video.srcObject;
  if (stream instanceof MediaStream) stream.getTracks().forEach((t) => t.stop());
  video.srcObject = null;
}

export interface BarcodeScannerProps {
  open: boolean;
  /** Close button, Esc, a tap outside, or the page going to the background. */
  onClose: () => void;
  /** Called once with the barcode digits (a UPC-E already expanded to UPC-A). */
  onDetected: (digits: string) => void;
  /** Shows a "Type the number instead" button. */
  onTypeInstead?: (() => void) | undefined;
}

/**
 * Reads a UPC or EAN barcode with the rear camera, in a modal dialog. The ZXing reader is loaded
 * only when the dialog opens, so it stays out of the main bundle. The same number must be read
 * twice in a row before it counts, which filters out a misread from a blurry frame. The camera is
 * turned off as soon as a code is read, the dialog closes, or the page goes to the background.
 * Needs https and the camera allowed by the site's Permissions-Policy.
 */
export function BarcodeScanner({ open, onClose, onDetected, onTypeInstead }: BarcodeScannerProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const detectedRef = useRef(onDetected);
  const closeRef = useRef(onClose);
  const ids = useId();
  const [starting, setStarting] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    detectedRef.current = onDetected;
    closeRef.current = onClose;
  }, [onDetected, onClose]);

  // Open and close the native dialog from the `open` prop.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  // The page behind a modal still scrolls on iPhone; hold it while the scanner is open.
  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = 'hidden';
    const onHide = () => {
      if (document.visibilityState === 'hidden') closeRef.current();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      root.style.overflow = before;
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [open]);

  // Start the camera while open; everything is torn down when it closes.
  useEffect(() => {
    if (!open) return;
    let stopped = false;
    let controls: { stop: () => void } | null = null;
    let last = '';
    let sameReads = 0;
    setStarting(true);
    setProblem(null);

    const start = async () => {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        const err = new Error('insecure context');
        err.name = 'InsecureContext';
        throw err;
      }
      const [{ BrowserMultiFormatOneDReader }, { BarcodeFormat, DecodeHintType }] =
        await Promise.all([import('@zxing/browser'), import('@zxing/library')]);
      const video = videoRef.current;
      if (stopped || !video) return;
      const hints = new Map([
        [
          DecodeHintType.POSSIBLE_FORMATS,
          [BarcodeFormat.UPC_A, BarcodeFormat.EAN_13, BarcodeFormat.UPC_E, BarcodeFormat.EAN_8],
        ],
      ]);
      const reader = new BrowserMultiFormatOneDReader(hints, {
        delayBetweenScanAttempts: 120,
        delayBetweenScanSuccess: 250,
      });
      const started = await reader.decodeFromConstraints(
        {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        },
        video,
        (result) => {
          if (!result || stopped) return;
          let text = result.getText();
          if (result.getBarcodeFormat() === BarcodeFormat.UPC_E) text = expandUpcE(text) ?? text;
          if (!/^\d{8,14}$/.test(text)) return;
          sameReads = text === last ? sameReads + 1 : 1;
          last = text;
          if (sameReads < 2) return;
          stopped = true;
          controls?.stop();
          releaseVideo(videoRef.current);
          detectedRef.current(text);
        },
      );
      if (stopped) {
        started.stop();
        releaseVideo(video);
        return;
      }
      controls = started;
      setStarting(false);
    };

    start().catch((err: unknown) => {
      if (stopped) return;
      releaseVideo(videoRef.current);
      setStarting(false);
      setProblem(cameraProblem(err));
    });

    return () => {
      stopped = true;
      controls?.stop();
      releaseVideo(videoRef.current);
    };
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="crave-scan"
      aria-labelledby={`${ids}-title`}
      aria-describedby={`${ids}-text`}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // A tap on the backdrop lands on the dialog element itself.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <style href="crave-barcode-scanner" precedence="default">
        {CSS}
      </style>
      <div className="crave-scan-body">
        <h2 id={`${ids}-title`} className="crave-scan-title">
          Scan the barcode
        </h2>
        <p id={`${ids}-text`} className="crave-scan-text">
          Hold the barcode flat inside the box, about a hand away. It reads by itself.
        </p>
        {open && (
          <div className="crave-scan-view">
            <video ref={videoRef} playsInline muted autoPlay aria-label="Camera view" />
            {!problem && <div className="crave-scan-guide" aria-hidden="true" />}
            {starting && !problem && (
              <p className="crave-scan-status" aria-live="polite">
                Starting the camera…
              </p>
            )}
          </div>
        )}
        {problem && (
          <div className="crave-scan-alert" role="alert">
            {problem}
          </div>
        )}
        <div className="crave-scan-actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          {onTypeInstead && (
            <Button
              type="button"
              variant={problem ? 'primary' : 'secondary'}
              onClick={onTypeInstead}
            >
              Type the number instead
            </Button>
          )}
        </div>
      </div>
    </dialog>
  );
}
